import { QuotePdfDialog, Quote } from '@paintscout/react-quote'
import { QuoteLinkDialog } from 'src/components/dialogs'
import type { DialogProps, StyleClasses } from '@paintscout/ui'
import ClearIcon from '@material-ui/icons/Clear'
import type { QuoteDocument } from 'paintscout'
import { goToQuote } from 'src/util/routing'
import { useFlags } from 'shared/components/common/FlagsProvider'
import React, { useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  IconButton,
  Tooltip,
  LinkIcon,
  DownloadIcon,
  getEnabledTranslationLangs,
  LANGUAGE_LABELS,
  useTranslation
} from '@paintscout/ui'
import { useDialogs, useClientOptions } from '@paintscout/ui-business'
import { useWorkOrderViewDialogStyles } from './WorkOrderViewDialogStyles'

const WORK_ORDER_VIEW = 'work-order'
const DOWNLOAD_PDF_LABEL = 'Download PDF'

export interface WorkOrderViewDialogDialogProps extends DialogProps {
  classes?: DialogProps['classes'] & StyleClasses<typeof useWorkOrderViewDialogStyles>
  quote: QuoteDocument
  scrollToBottom?: boolean
  onCancel: () => void
}

function WorkOrderViewDialog(props: WorkOrderViewDialogDialogProps) {
  const { onCancel, quote, scrollToBottom, ...baseDialogProps } = props
  const { openDialog, dismissDialog } = useDialogs()
  const { options, clientId } = useClientOptions()
  const classes = useWorkOrderViewDialogStyles()
  const { translations } = useFlags()
  const { __ } = useTranslation()
  const availableLangs = getEnabledTranslationLangs(translations)

  useEffect(() => {
    if (!scrollToBottom) return
    const timer = setTimeout(() => {
      document
        .getElementById('work-order-dialog-content')
        ?.scrollTo({ top: document.getElementById('work-order-dialog-content')?.scrollHeight, behavior: 'smooth' })
    }, 500)
    return () => clearTimeout(timer)
  }, [scrollToBottom])

  function openPdfDialog(lang?: string) {
    openDialog(QuotePdfDialog, {
      quote,
      companyId: clientId,
      options,
      silent: true,
      view: WORK_ORDER_VIEW,
      lang,
      onConfirm: dismissDialog
    })
  }

  const rightContent = (
    <div className={classes.closeIcons}>
      <IconButton
        color="inherit"
        aria-label={__('Edit')}
        onClick={() => goToQuote({ id: quote._id, invoice: true, params: { view: WORK_ORDER_VIEW } })}
      >
        <Typography variant="body2">{__('Edit')}</Typography>
      </IconButton>
      <IconButton
        color="inherit"
        aria-label={__('Link')}
        onClick={() => openDialog(QuoteLinkDialog, { view: WORK_ORDER_VIEW, onConfirm: dismissDialog, quote })}
      >
        <LinkIcon />
      </IconButton>
      <Tooltip title={__(DOWNLOAD_PDF_LABEL)}>
        <IconButton color="inherit" aria-label={__(DOWNLOAD_PDF_LABEL)} onClick={() => openPdfDialog()}>
          <DownloadIcon />
        </IconButton>
      </Tooltip>
      {availableLangs.map((l) => (
        <Tooltip key={l} title={`${__(DOWNLOAD_PDF_LABEL)} (${LANGUAGE_LABELS[l]})`}>
          <IconButton
            color="inherit"
            aria-label={`${__(DOWNLOAD_PDF_LABEL)} (${LANGUAGE_LABELS[l]})`}
            onClick={() => openPdfDialog(l)}
          >
            <Typography variant="body2">{l.toUpperCase()}</Typography>
          </IconButton>
        </Tooltip>
      ))}
      <IconButton color="inherit" aria-label={__('Close')} onClick={onCancel}>
        <ClearIcon />
      </IconButton>
    </div>
  )

  return (
    <Dialog fullWidth={true} maxWidth="lg" {...baseDialogProps}>
      <DialogTitle classes={{ root: classes.dialogTitle }} rightContent={rightContent}>
        <Typography variant="h2" weight={'normal'}>
          {__('Work Order')}
        </Typography>
      </DialogTitle>
      <DialogContent id="work-order-dialog-content" classes={{ root: classes.dialogContent }}>
        <Quote quote={quote} view={WORK_ORDER_VIEW} />
      </DialogContent>
    </Dialog>
  )
}

export default WorkOrderViewDialog
